import React, { useState, useEffect } from 'react';
import { drillsAPI, tagsAPI } from '../services/api';
import './GameSelectionDialog.css';

const QUESTION_COUNT_OPTIONS = [
  { value: 5, label: '5 questions', description: 'Short drill' },
  { value: 10, label: '10 questions', description: 'Standard drill' }
];
const FALLBACK_LLM_MODELS = ['gpt-4.1'];
const DRILL_PLACE_OPTIONS = ['Gig', 'Sports', 'Pub/Bar', 'Karaoke', 'Street', 'Airport'];
const DRILL_STAGE_OPTIONS = ['Cold approach initial', 'Mid Set', 'Date'];

const createEmptyDrillContext = () => ({
  place: '',
  resistanceLevel: '',
  stage: '',
});

const GameSelectionDialog = ({
  gameType,
  onClose,
  onStart,
  showQuestionCountSelector = false,
  showDrillContextSelector = false
}) => {
  const [tags, setTags] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]); // For cue game multi-select
  const [selectedQuestionCount, setSelectedQuestionCount] = useState(10);
  const isLoading = false;
  const [searchQuery, setSearchQuery] = useState(''); // Search query for filtering tags
  const [drillContext, setDrillContext] = useState(createEmptyDrillContext);
  const [llmModels, setLlmModels] = useState(FALLBACK_LLM_MODELS);
  const [selectedLlmModel, setSelectedLlmModel] = useState(FALLBACK_LLM_MODELS[0]);

  useEffect(() => {
    loadTags();
    loadLlmModels();
  }, []);

  const loadTags = async () => {
    try {
      const response = await tagsAPI.getAll();
      setTags(response.results || []);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const loadLlmModels = async () => {
    try {
      const response = await drillsAPI.getLLMModels();
      const models = Array.isArray(response.models) && response.models.length > 0
        ? response.models
        : FALLBACK_LLM_MODELS;
      setLlmModels(models);
      setSelectedLlmModel(response.default_model || models[0]);
    } catch (error) {
      console.error('Error loading LLM models:', error);
    }
  };

  const handleStart = () => {
    if (gameType === 'cue') {
      // Cue game requires at least one tag
      if (selectedTagIds.length === 0) {
        alert('Please select at least one tag to practice');
        return;
      }
      // Primary tag is the first selected, additional tags are the rest
      const primaryTagId = selectedTagIds[0];
      const additionalTagIds = selectedTagIds.slice(1);
      onStart(primaryTagId, gameType, additionalTagIds, selectedQuestionCount, drillContext, selectedLlmModel);
    } else {
      // Drill game requires single tag
      if (!selectedTagId) {
        alert('Please select a tag to practice');
        return;
      }
      onStart(selectedTagId, gameType, [], selectedQuestionCount, drillContext, selectedLlmModel);
    }
  };

  const getGameTitle = () => {
    switch (gameType) {
      case 'recall':
        return 'Recall Game';
      case 'cue':
        return 'Cue Game';
      default:
        return 'Practice Game';
    }
  };

  const getGameDescription = () => {
    switch (gameType) {
      case 'recall':
        return 'Practice recalling and adapting your lines from memory';
      case 'cue':
        return 'Practice responding to conversation cues and prompts. Select multiple tags to generate varied scenarios.';
      default:
        return 'Start your practice session';
    }
  };

  const toggleTagSelection = (tagId) => {
    if (gameType === 'cue') {
      setSelectedTagIds(prev => {
        if (prev.includes(tagId)) {
          return prev.filter(id => id !== tagId);
        } else {
          return [...prev, tagId];
        }
      });
    } else {
      setSelectedTagId(tagId);
    }
  };

  const isTagSelected = (tagId) => {
    if (gameType === 'cue') {
      return selectedTagIds.includes(tagId);
    } else {
      return selectedTagId === tagId;
    }
  };

  // Recall drills must only show tags that have at least one line.
  const candidateTags = gameType === 'recall'
    ? tags.filter(tag => (tag.line_count || 0) > 0)
    : tags;

  // Filter tags based on search query (matches beginning of words)
  const filteredTags = candidateTags.filter(tag => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const tagName = tag.name.toLowerCase();
    // Match if query matches beginning of tag name or any word in tag name
    const words = tagName.split(/\s+/);
    return words.some(word => word.startsWith(query)) || tagName.startsWith(query);
  });

  return (
    <div className="game-selection-dialog-overlay">
      <div className="game-selection-dialog">
        <div className="dialog-header">
          <h3>{getGameTitle()}</h3>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="dialog-content">
          <p className="game-description">{getGameDescription()}</p>
          
          {gameType === 'cue' ? (
            <div className="tag-selection">
              <label>Select one or more tags to practice:</label>
              <div className="tag-search-container">
                <input
                  type="text"
                  className="tag-search-input"
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="tag-multi-select">
                {filteredTags.length > 0 ? (
                  filteredTags.map(tag => (
                    <div
                      key={tag.id}
                      className={`tag-option ${isTagSelected(tag.id) ? 'selected' : ''}`}
                      onClick={() => toggleTagSelection(tag.id)}
                    >
                      <input
                        type="checkbox"
                        checked={isTagSelected(tag.id)}
                        onChange={() => toggleTagSelection(tag.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="tag-name">{tag.name}</span>
                    </div>
                  ))
                ) : (
                  <div className="no-tags-found">
                    <p>No tags found matching "{searchQuery}"</p>
                  </div>
                )}
              </div>
              {selectedTagIds.length > 0 && (
                <p className="selection-info">
                  {selectedTagIds.length} tag{selectedTagIds.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          ) : (
            <div className="tag-selection">
              <label htmlFor="tag-select">Select a tag to practice:</label>
              {filteredTags.length > 0 && (
                <div className="tag-search-container">
                  <input
                    type="text"
                    className="tag-search-input"
                    placeholder="Search tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              )}
              {gameType === 'recall' && filteredTags.length === 0 && (
                <div className="no-tags-found">
                  <p>No tags with lines are available for recall yet.</p>
                </div>
              )}
              <select
                id="tag-select"
                value={selectedTagId}
                onChange={(e) => setSelectedTagId(e.target.value)}
                className="tag-dropdown"
              >
                <option value="">Choose a tag...</option>
                {filteredTags.map(tag => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name} ({tag.line_count || 0} lines)
                  </option>
                ))}
              </select>
            </div>
          )}

          {showQuestionCountSelector && (
            <div className="question-count-selection">
              <span className="question-count-label">How many questions?</span>
              <div className="question-count-options" role="radiogroup" aria-label="Question count">
                {QUESTION_COUNT_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`question-count-option ${selectedQuestionCount === option.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="question-count"
                      value={option.value}
                      checked={selectedQuestionCount === option.value}
                      onChange={() => setSelectedQuestionCount(option.value)}
                      aria-label={option.label}
                    />
                    <span className="question-count-copy">
                      <span className="question-count-title">{option.label}</span>
                      <span className="question-count-description">{option.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="model-selection">
            <label htmlFor="llm-model-select">Model</label>
            <select
              id="llm-model-select"
              value={selectedLlmModel}
              onChange={(e) => setSelectedLlmModel(e.target.value)}
              className="model-select"
            >
              {llmModels.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </select>
          </div>

          {showDrillContextSelector && (
            <div className="drill-context-selection">
              <span className="drill-context-selection-label">Optional drill context</span>
              <div className="drill-context-selection-grid">
                <label className="drill-context-selection-field">
                  <span>Place</span>
                  <select
                    value={drillContext.place}
                    onChange={(e) => setDrillContext(prev => ({ ...prev, place: e.target.value }))}
                    className="drill-context-select"
                  >
                    <option value="">Optional</option>
                    {DRILL_PLACE_OPTIONS.map((placeOption) => (
                      <option key={placeOption} value={placeOption}>
                        {placeOption}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="drill-context-selection-field">
                  <span>Resistance Level</span>
                  <select
                    value={drillContext.resistanceLevel}
                    onChange={(e) => setDrillContext(prev => ({ ...prev, resistanceLevel: e.target.value }))}
                    className="drill-context-select"
                  >
                    <option value="">Optional</option>
                    {Array.from({ length: 11 }, (_, index) => (
                      <option key={index} value={index}>
                        {index}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="drill-context-selection-field">
                  <span>Stage</span>
                  <select
                    value={drillContext.stage}
                    onChange={(e) => setDrillContext(prev => ({ ...prev, stage: e.target.value }))}
                    className="drill-context-select"
                  >
                    <option value="">Optional</option>
                    {DRILL_STAGE_OPTIONS.map((stageOption) => (
                      <option key={stageOption} value={stageOption}>
                        {stageOption}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="dialog-actions">
          <button 
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleStart}
            disabled={(gameType === 'cue' ? selectedTagIds.length === 0 : !selectedTagId) || isLoading}
          >
            {isLoading ? 'Starting...' : 'Proceed'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameSelectionDialog;
